import { MAIN_TYPES } from "../Reducers/MainReducer";
import {
  ExamInterface,
  ExamSettingsInterface,
  ExerciseInterface,
  ObjectiveInterface,
} from "../../../interfaces/examInterfaces";
import { cloneDeep, isEqual } from "lodash";
import firebaseExamService from "../../../firebase/firebaseExamService";
import { DIALOG_TYPES, DIALOGS } from "../../Dialogs/dialogReducer";
import { PRIVATE_ROUTES } from "../../../constants";
import { NavigateFunction } from "react-router-dom";
import { getAppDispatch } from "../../../stores/dispatchStore";
import { ExerciseHeight } from "../ExamBuilder";
import { MANIPULATE_OBJECTIVES_TYPES, Tab } from "../Reducers/ManipulateObjectivesReducer";
import { SnackbarCloseReason } from "@mui/material";
import * as C from "../../../constants";
import { DEFAULT_LAYOUT_EXAM, LayoutExamLabel, LayoutExamTypeKeys } from "../../../enums/layoutExamTypes";

interface TimestampLike {
  seconds: number;
  nanoseconds: number;
}

export function checkValidLicense(
  examAccess: { [grade: number]: { validFrom: TimestampLike; validTo: TimestampLike } },
  grade: number,
  navigate: NavigateFunction,
) {
  const validGrades = examAccess
    ? Object.keys(examAccess)
        .filter((key) => {
          const numericKey = +key;
          const fromDate = new Date(
            examAccess[numericKey].validFrom.seconds * 1000 + examAccess[numericKey].validFrom.nanoseconds / 1000000,
          );
          const toDate = new Date(
            examAccess[numericKey].validTo.seconds * 1000 + examAccess[numericKey].validTo.nanoseconds / 1000000,
          );
          const now = new Date();
          return fromDate < now && toDate > now;
        })
        .map((grade) => +grade)
    : null;

  const userHasAccess = validGrades?.includes(grade);
  if (!userHasAccess) {
    navigate("/exam-overview");
  }
}

export function handleOpenDialog(dialogName: string) {
  const dispatch = getAppDispatch();
  dispatch({ type: DIALOG_TYPES.OPEN_DIALOG, payload: { dialogName } });
}

export function handleCloseDialog(dialogName: string) {
  const dispatch = getAppDispatch();
  dispatch({ type: DIALOG_TYPES.CLOSE_DIALOG, payload: { dialogName } });
}

export function handleClickTab(objectiveId: string, index: number, selectedObjectiveId: string) {
  if (objectiveId !== selectedObjectiveId) {
    setActiveTab(index);
    resetDataPages();
    setSelectedObjectiveId(objectiveId);
  }
}

export function sortObjectives(objectivesArray: ObjectiveInterface[]) {
  return objectivesArray.sort((a, b) => {
    const aParsed = parseId(a.id);
    const bParsed = parseId(b.id);

    return (
      aParsed.class - bParsed.class ||
      aParsed.group - bParsed.group ||
      aParsed.topic - bParsed.topic ||
      aParsed.objective - bParsed.objective
    );
  });
}

function parseId(id: string) {
  const match = id.match(/^(\d)(\d)(\d{1,2})(\d)$/);
  if (!match) {
    throw new Error(`Invalid ID format: ${id}`);
  }

  const [, classId, groupId, topicId, objectiveId] = match;
  return {
    class: +classId,
    group: +groupId,
    topic: +topicId,
    objective: +objectiveId,
  };
}

export function resetDataPages() {
  setPages([[]]);
  setPagesCreated(false);
  setIsMeasuringContainer(true);
  setIsMeasuringExercises(false);
}

function updateLocalStorage(examData: ExamInterface) {
  const persistedState = localStorage.getItem("persist:root");

  if (persistedState) {
    const parsedState = JSON.parse(persistedState);
    parsedState.examBuilder = examData;
    localStorage.setItem("persist:root", JSON.stringify(parsedState));
  }
}

export async function updateExamSettings(
  userId: string,
  printSolutions: boolean = true,
  createPageNumbers: boolean = true,
  selectedLayout: LayoutExamTypeKeys = DEFAULT_LAYOUT_EXAM,
) {
  const examSettings: ExamSettingsInterface = { printSolutions, createPageNumbers, selectedLayout };
  return await firebaseExamService.updateExamSettings({ userId, examSettings });
}

export async function saveExam(
  userId: string,
  nameChanged: boolean,
  examData: ExamInterface,
  pathString: string,
  isDemoMode?: boolean,
) {
  setOpenSnackbarSave(true);
  if (userId && !isDemoMode) {
    if (nameChanged) {
      const examId = examData.id;
      const name = examData.name;
      firebaseExamService.renameExam({ userId, pathString, examId, name });
    }
    await firebaseExamService.updateExamSubcolletion({ userId, exam: examData });
    overwriteObjectives(cloneDeep(examData.objectives));
    updateLocalStorage(examData);
    setInitialExamData(examData);
    setOpenSnackbarSave(false);
    setOpenSnackbarSaved(true);
    setHasUnsavedChanges(false);
  }
}

export function checkChangesLeavePage(
  hasUnsavedChanges: boolean,
  navigate: NavigateFunction,
  pathString: string,
  isDemoMode: boolean,
) {
  if (isDemoMode) {
    navigate(C.PUBLIC_ROUTES.LANDING);
    return;
  }
  if (hasUnsavedChanges) {
    const targetPath = PRIVATE_ROUTES.EXAM_OVERVIEW + pathString;
    setTargetPath(targetPath);
    handleOpenDialog(DIALOGS.DISCARD_CHANGES_EXAM_DIALOG);
  } else {
    navigateExamOverview(navigate, pathString);
  }
}

export function checkChanges(initalExamData: ExamInterface, examData: ExamInterface) {
  const changes = !isEqual(initalExamData, examData);
  setHasUnsavedChanges(changes);
}

function navigateExamOverview(navigate: NavigateFunction, pathString: string) {
  handleCloseDialog(DIALOGS.DISCARD_CHANGES_EXAM_DIALOG);
  navigate("/exam-overview" + pathString);
}

export function renameExam(newName: string, examData?: ExamInterface) {
  if (examData) {
    setExamData({ name: newName }, examData);
    setNameChanged(true);
  } else {
    console.warn("renameExam called without examData – skipping local update.");
  }

  handleCloseDialog(DIALOGS.RENAME_EXAM_DIALOG);
}

export function handleCloseSnackbarSave(event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) {
  if (reason === "clickaway") {
    return;
  }
  setOpenSnackbarSave(false);
}

export function handleCloseSnackbarSaved(event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) {
  if (reason === "clickaway") {
    return;
  }
  setOpenSnackbarSaved(false);
}

function setTargetPath(targetPath: string) {
  const dispatch = getAppDispatch();
  dispatch({ type: DIALOG_TYPES.SET_TARGET_PATH, payload: { targetPath: targetPath } });
}

export function setSelectedObjectiveId(objectiveId: string) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.SELECTED_OBJECTIVE_ID, payload: objectiveId });
}

export function setExamData(updatedFields: Partial<ExamInterface>, examData: ExamInterface) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.EXAM_DATA,
    payload: {
      ...examData,
      ...updatedFields,
    },
  });
}

export function setInitialExamData(initialExamData: ExamInterface) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.INITIAL_DATA,
    payload: {
      ...initialExamData,
    },
  });
}

export function setActiveTab(activeTab: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.ACTIVE_TAB, payload: activeTab });
}

export function setPages(pages: ExerciseInterface[][]) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.PAGES, payload: pages });
}

export function setPagesCreated(pagescreated: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.PAGES_CREATED, payload: pagescreated });
}

export function setTotalPoints(totalPoints: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.TOTAL_POINTS, payload: totalPoints });
}

export function setExercisesCopy(exercisesCopy: ExerciseInterface[]) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.EXERCISES_COPY, payload: exercisesCopy });
}

function setIsMeasuringContainer(isMeasuringContainer: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.IS_MEASURING_CONTAINER, payload: isMeasuringContainer });
}

function setIsMeasuringExercises(isMeasuringExercises: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.IS_MEASURING_EXERCISES, payload: isMeasuringExercises });
}

export function setExerciseHeights(exerciseHeights: Record<string, number>, heightData: ExerciseHeight) {
  const dispatch = getAppDispatch();
  if (heightData && exerciseHeights) {
    if (!(heightData.id in exerciseHeights)) {
      dispatch({
        type: MAIN_TYPES.EXERCISE_HEIGHTS,
        payload: {
          ...exerciseHeights,
          [heightData.id]: heightData.height,
        },
      });
    }
  } else {
    console.error("Heihgt data:", heightData, "exerciseHeights:", exerciseHeights);
  }
}

export function setFirstPageLoad(firstPageLoad: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.FIRST_PAGE_LOAD, payload: firstPageLoad });
}

export function setOpenSnackbarSave(openSnackbarSave: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.OPEN_SNACKBAR_SAVE, payload: openSnackbarSave });
}

export function setOpenSnackbarSaved(openSnackbarSaved: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.OPEN_SNACKBAR_SAVED, payload: openSnackbarSaved });
}

export function setNameChanged(nameChanged: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.NAME_CHANGED, payload: nameChanged });
}

export function setHasUnsavedChanges(hasUnsavedChanges: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.HAS_UNSAVED_CHANGES, payload: hasUnsavedChanges });
}

export function addObjectiveCopy(objective: ObjectiveInterface) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.ADD_OBJECTIVE,
    payload: objective,
  });
}

export function removeObjectiveCopy(objectiveId: number) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.REMOVE_OBJECTIVE,
    payload: objectiveId,
  });
}

export function overwriteObjectives(newObjectives: { [key: string]: ObjectiveInterface }) {
  const dispatch = getAppDispatch();
  dispatch({
    type: MAIN_TYPES.OVERWRITE_OBJECTIVES,
    payload: newObjectives,
  });
}

export function setMappedTabs(mappedTabs: Tab[] | null) {
  const dispatch = getAppDispatch();
  dispatch({ type: MANIPULATE_OBJECTIVES_TYPES.MAPPED_TABS, payload: mappedTabs });
}

export function setZoomFactor(newValue: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.ZOOM_FACTOR, payload: newValue });
}

export function setScrollHeight(currentScrollHeight: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.SCROLL_HEIGHT, payload: currentScrollHeight });
}
